<template>
  <div class="fullscreen-slider">
    <div class="fullscreen-slider-wrapper" :key="slides.length">
      <div class="navigation">
        <div v-if="showNavigationButtons" class="slider-button prev" @click="slider?.prev()">
          <IconChevronLeftLarge :size="24" />
        </div>
        <div v-if="showNavigationButtons" class="slider-button next" @click="slider?.next()">
          <IconChevronRightLarge :size="24" />
        </div>
        <div class="slider-button close" @click="onClose">
          <IconClose :size="24" />
        </div>
      </div>
      <div ref="sliderContainerRef" class="keen-slider">
        <div v-for="(photo, index) in slides" :key="index" class="keen-slider__slide">
          <div class="slide">
            <div class="shadow" />
            <div class="slide-inner">
              <img class="image" :src="photo.high" :alt="alt" />
              <div class="caption">
                <div class="top-heading">
                  <div>
                    <template v-for="n in 5" :key="`star-${n}`">
                      <IconRating
                        v-if="n <= photo.feedback.rating"
                        :size="24"
                        color="rating-accented"
                      />
                      <IconRating v-else :size="24" color="rating-neutral" />
                    </template>
                  </div>
                  <div class="date">
                    {{ trueDate(photo.feedback.dateCreated) }}
                  </div>
                </div>
                <div class="sku">{{ computeSkuText(photo.feedback) }}</div>
                <div class="content">
                  {{ photo.feedback.content }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useKeenSlider } from 'keen-slider/vue';
import {
  IconRating,
  IconChevronLeftLarge,
  IconChevronRightLarge,
  IconClose,
} from '@/features/ui-kit/icons';
import { formatDeliveryDate, OverflowHiddenController } from '@/shared/helpers';

// TODO: Replace with proper GraphQL types when available
interface Feedback {
  id: string;
  rating: number;
  dateCreated: string;
  content: string;
  sku: {
    characteristicValues: Array<{
      characteristic: { title: string };
      title: string;
    }>;
  };
}

interface Photo {
  high: string;
  low: string;
  original: string;
  thumbnail: string;
}

interface Slide extends Photo {
  feedback: Feedback;
}

interface Props {
  slides: Slide[];
  index?: number;
  alt?: string;
}

interface Emits {
  (e: 'close', data: { activeIndex: number }): void;
  (e: 'change', feedbackId: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  slides: () => [],
  index: 0,
  alt: '',
});

const emit = defineEmits<Emits>();

let slider: any = null;

const [sliderContainerRef] = useKeenSlider(
  {
    loop: props.slides.length > 1,
    initial: props.index,
    slides: {
      perView: 1,
    },
  },
  [
    (sliderInstance) => {
      slider = sliderInstance;
    },
  ]
);

const showNavigationButtons = computed(() => {
  return props.slides.length > 1;
});

const onClose = () => {
  emit('close', {
    activeIndex: slider?.track?.details?.abs || 0,
  });
};

const keyHandler = (e: KeyboardEvent) => {
  switch (e.keyCode) {
    case 27: // ESC
      onClose();
      break;
    case 37: // Left arrow
      slider?.prev();
      break;
    case 39: // Right arrow
      slider?.next();
      break;
  }
};

const computeDate = (datePublished: string) => {
  const published = new Date(datePublished);

  if (published.getFullYear() === new Date().getFullYear()) {
    return formatDeliveryDate(published, true);
  } else {
    return formatDeliveryDate(published, true) + ' ' + published.getFullYear();
  }
};

const computeSkuText = (feedback: Feedback): string => {
  return feedback.sku.characteristicValues
    .map((char) => `${char.characteristic.title}: ${char.title}`)
    .join(' • ');
};

const slideChangeFeedback = () => {
  if (slider?.track?.details?.abs !== undefined && props.slides[slider.track.details.abs]) {
    emit('change', props.slides[slider.track.details.abs].feedback.id);
  }
};

const trueDate = (data: string) => {
  const published = new Date(data);

  if (published.getFullYear() === new Date().getFullYear()) {
    return formatDeliveryDate(published, true);
  } else {
    return formatDeliveryDate(published, true) + ' ' + published.getFullYear();
  }
};

onMounted(async () => {
  await nextTick();
  if (sliderContainerRef.value) {
    // Initialize keen slider
    const keenSlider = require('keen-slider');
    const keenSliderInstance = keenSlider.default(sliderContainerRef.value, {
      loop: props.slides.length > 1,
      initial: props.index,
      slides: {
        perView: 1,
      },
    });

    keenSliderInstance.on('slideChanged', slideChangeFeedback);
    slider = keenSliderInstance;
  }

  window.addEventListener('keyup', keyHandler);
  OverflowHiddenController.enable();
});

onUnmounted(() => {
  window.removeEventListener('keyup', keyHandler);
  if (slider) {
    slider.destroy();
  }
  OverflowHiddenController.disable();
});
</script>

<style lang="stylus" scoped>
.fullscreen-slider
  overscroll-behavior none
  position fixed
  top 0
  left 0
  right 0
  bottom 0
  z-index 201
  background: var(--black-a-70, rgba(0, 0, 0, 0.70));

  .fullscreen-slider-wrapper
    .navigation
      width 100%
      .slider-button
        Button()
        position absolute
        top calc(50% - 20px)
        z-index 2
        border-radius 20px
        border 1px solid Gray(A30)
        background #fff
        transition all .3s ease-in-out
        opacity .5
        width 40px
        height 40px
        display flex
        align-items center
        justify-content center
        backdrop-filter blur(20px)
        -webkit-backdrop-filter blur(20px)
        cursor pointer

        &.next
          right 16px
        &.prev
          left 16px
        &.close
          right 16px
          top 16px
        &:hover
          opacity .7
        &:active
          opacity .9

    margin 0 auto
    border-radius 8px
    transform translateZ(0)

    .keen-slider
      .keen-slider__slide
        .slide
          display flex
          user-select none
          margin 0 auto
          z-index 1
          position relative
          height 100vh
          width calc(100vh / var(--photo-ratio))

          +Media(Mobile, Tablet)
            width 100%
            display flex
            align-items center

          .image
            width auto
            max-width 100vw
            height 100%
            display block
            user-select none
            pointer-events none
            background #EEE
            object-fit contain

            +Media(Mobile, Tablet)
              height initial
              position: relative;
              top 50%;
              transform translateY(-50%)

          .caption
            position absolute
            bottom 0
            padding 40px 12px
            background: linear-gradient(0deg, #000 0.2%, rgba(0, 0, 0, 0.99) 6.84%, rgba(0, 0, 0, 0.96) 13.48%, rgba(0, 0, 0, 0.92) 20.12%, rgba(0, 0, 0, 0.85) 26.76%, rgba(0, 0, 0, 0.77) 33.4%, rgba(0, 0, 0, 0.67) 40.04%, rgba(0, 0, 0, 0.56) 46.68%, rgba(0, 0, 0, 0.44) 53.32%, rgba(0, 0, 0, 0.33) 59.96%, rgba(0, 0, 0, 0.23) 66.6%, rgba(0, 0, 0, 0.15) 73.24%, rgba(0, 0, 0, 0.08) 79.88%, rgba(0, 0, 0, 0.04) 86.52%, rgba(0, 0, 0, 0.01) 93.16%, rgba(0, 0, 0, 0.00) 99.8%);
            left -1px
            right -1px

          .slide-inner
            position relative
            display block
            margin: 0 auto;
            width: auto;
            max-width: 100vw;
            height: 100%;
            min-width: 320px;

.stars
  margin-bottom 22px

.sku
  color: #FFFFFFB2;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.084px;
  margin-top 16px
  margin-bottom 12px

.content
  color: var(--light-text-inverse-primary, #FFF);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.07px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;

.shadow
  position absolute
  bottom 0
  width: 100%
  height 100%
  max-height 384px
  background: linear-gradient(0deg, #000 0.2%, rgba(0, 0, 0, 0.99) 6.84%, rgba(0, 0, 0, 0.96) 13.48%, rgba(0, 0, 0, 0.92) 20.12%, rgba(0, 0, 0, 0.85) 26.76%, rgba(0, 0, 0, 0.77) 33.4%, rgba(0, 0, 0, 0.67) 40.04%, rgba(0, 0, 0, 0.56) 46.68%, rgba(0, 0, 0, 0.44) 53.32%, rgba(0, 0, 0, 0.33) 59.96%, rgba(0, 0, 0, 0.23) 66.6%, rgba(0, 0, 0, 0.15) 73.24%, rgba(0, 0, 0, 0.08) 79.88%, rgba(0, 0, 0, 0.04) 86.52%, rgba(0, 0, 0, 0.01) 93.16%, rgba(0, 0, 0, 0.00) 99.8%);

.top-heading
  display flex
  justify-content space-between
  align-items center
  color: var(--light-text-inverse-primary, #FFF);

.date
  color: #FFFFFFB2;

.transparent
  opacity 0.3
</style>
